import styled, { css } from 'styled-components';
import { ANIMATION_DURATION, getToken } from '@optimus-web/common';
import { Icon } from '../Icon';
import { ImageProps, ImageRatio, PlaceholderProps } from './ImageProps';

export const placeholderIconSize = 40;

interface WithRatio {
    isRatioDefined: boolean;
}

export interface ImageElementProps extends WithRatio {
    isLoaded: boolean;
    shouldShowLoader?: boolean;
    hidden?: boolean;
    grayscale?: boolean;
    objectFit?: 'contain' | 'cover';
    objectPosition?: string;
    rounded?: boolean;
    block?: boolean;
}

export const ImageElement = styled.img<ImageElementProps>`
    background: ${(props) => (props.isLoaded ? 'transparent' : getToken('backgroundStaticFlat'))};
    opacity: ${(props) => (props.isLoaded ? 1 : 0)};
    transition: ${ANIMATION_DURATION.default}ms opacity ease-out;
    max-width: 100%;
    ${(props) =>
        (props.shouldShowLoader && !props.isLoaded) || props.hidden
            ? css`
                  visibility: hidden;
                  height: 1px !important;
                  width: 1px !important;
              `
            : props.height && `height: ${props.height};`}
    ${({ grayscale }) =>
        grayscale &&
        css`
            filter: grayscale(1);
        `}
    ${({ objectFit }) =>
        objectFit &&
        css`
            object-fit: ${objectFit};
        `}
    ${({ objectPosition }) =>
        objectPosition &&
        css`
            object-position: ${objectPosition};
        `}
    ${({ rounded }) =>
        rounded &&
        css`
            border-radius: ${getToken('borderRadius50')}px;
        `}

    ${({ isRatioDefined }) =>
        isRatioDefined &&
        css`
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
        `}

    ${({ block }) =>
        block &&
        css`
            display: block;
            width: 100%;
        `}
`;

type ImagePlaceholderProps = PlaceholderProps & Pick<ImageProps, 'rounded'> & WithRatio;

export const ImagePlaceholder = styled.div<ImagePlaceholderProps>`
    min-height: ${(props) => props.minHeight || placeholderIconSize}px;
    min-width: ${(props) => props.minWidth || placeholderIconSize}px;
    background-color: ${(props) =>
        props.backgroundVariant === 'transparent' ? 'transparent' : getToken('backgroundDisabled')};
    position: relative;
    ${({ rounded }) =>
        rounded &&
        css`
            border-radius: ${getToken('borderRadius50')}px;
        `}

    ${({ isRatioDefined }) =>
        isRatioDefined &&
        css`
            position: absolute;
            inset: 0;
        `}
`;

interface ImagePlaceholderIconProps {
    iconSize?: number;
}

export const ImagePlaceholderIcon = styled(Icon)<ImagePlaceholderIconProps>`
    ${({ iconSize = placeholderIconSize }) => css`
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${iconSize}px;
        height: ${iconSize}px;
        svg {
            width: ${iconSize}px;
            height: ${iconSize}px;
        }
        path {
            fill: ${getToken('textDisabled')};
        }
    `}
`;

const calculateRatio = (ratio: ImageRatio) => {
    if (parseInt(ratio, 10) === 1) {
        return 100;
    }
    const arr = ratio.split('/').map((item) => parseInt(item, 10));
    return (arr[1] / arr[0]) * 100;
};

interface ImageWrapperProps {
    imageRatio?: ImageRatio;
}

export const ImageWrapper = styled.div<ImageWrapperProps>`
    position: relative;
    height: 0;
    ${({ imageRatio }) =>
        imageRatio &&
        css`
            padding-bottom: ${calculateRatio(imageRatio)}%;
        `};
`;

export const SpinnerWrapper = styled.div<WithRatio>`
    ${({ isRatioDefined }) =>
        isRatioDefined &&
        css`
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        `}
`;
